// แผงด้านข้าง "ดูรายการ" — รายการเต็มของ insight (snapshot เดียวกับไฟล์แนบ) โดยไม่ต้องออกจากหน้า
// คัดลอกรหัส (คอลัมน์แรก) หรือทั้งตาราง (วางใน Excel/SML ได้)
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, CircularProgress, Drawer, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Swal from 'sweetalert2';
import api from '../../adapters/api';
import { money } from './format';

const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

function formatCell(col, v) {
  if (v === null || v === undefined || v === '') return '';
  if (col.type === 'money') return money(v);
  if (col.type === 'percent') return `${Math.round(Number(v) * 100)}%`;
  if (col.type === 'number') return Number(v).toLocaleString('th-TH', { maximumFractionDigits: 2 });
  if (col.type === 'date') {
    const [y, m, d] = String(v).slice(0, 10).split('-').map(Number);
    return `${d} ${THAI_MONTHS[m - 1]} ${String(y + 543).slice(2)}`;
  }
  return String(v);
}

// ค่าดิบสำหรับคัดลอก (ตัวเลขไม่ย่อเป็น k/ลบ.)
function rawCell(col, v) {
  if (v === null || v === undefined) return '';
  if (col.type === 'money') return Math.round(Number(v));
  if (col.type === 'percent') return `${Math.round(Number(v) * 100)}%`;
  return String(v).replace(/[\t\n]/g, ' ');
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    Swal.fire({ icon: 'success', title: 'คัดลอกแล้ว', timer: 1200, showConfirmButton: false });
  } catch {
    Swal.fire({ icon: 'error', title: 'คัดลอกไม่สำเร็จ', text: 'เบราว์เซอร์ไม่อนุญาตให้คัดลอก', confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
  }
}

// "ตั้งใจจองไว้" รายใบ SO — ใช้ร่วมกันทุกคน ไม่แสดงใน insight อีก 60 วัน
async function reserveSo(taskId, docNo) {
  const ok = await Swal.fire({
    icon: 'question', title: `${docNo} ตั้งใจจองไว้?`,
    text: 'ใบสั่งขายนี้จะไม่แสดงใน insight ของทุกคนเป็นเวลา 60 วัน',
    showCancelButton: true, confirmButtonText: 'ยืนยัน', cancelButtonText: 'ยกเลิก',
    confirmButtonColor: '#1976d2', cancelButtonColor: '#757575',
  });
  if (!ok.isConfirmed) return false;
  try {
    await api.post(`/api/insight/tasks/${taskId}/suppress`, { doc_no: docNo });
    Swal.fire({ icon: 'success', title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false });
    return true;
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.error || err.message, confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
    return false;
  }
}

// ปุ่มท้ายแถวตามมุมของ insight (ตอนนี้มีแค่ "ตั้งใจจองไว้" ของ SO พร้อมออกบิล)
// preview = โหมดดูในฐานะผู้ใช้: แสดงปุ่มแต่กดไม่ได้
export function rowActionFor(task, preview = false) {
  if (task?.angle !== 'admin_so_ready') return undefined;
  return { label: 'ตั้งใจจองไว้', doneLabel: 'ตั้งใจจองไว้แล้ว', disabled: preview, onClick: row => reserveSo(task.id, row.doc_no) };
}

// rowAction (ไม่บังคับ) = { label, doneLabel, onClick(row) → Promise } — ปุ่มท้ายแถว เช่น "ตั้งใจจองไว้" ของ SO
export default function InsightRowsDrawer({ open, onClose, url, title, onDownload, onCopy, rowAction }) {
  const [doneRows, setDoneRows] = useState(() => new Set());
  const { data, isLoading, error } = useQuery({
    queryKey: ['insightRows', url],
    queryFn: async () => (await api.get(url)).data,
    enabled: open && Boolean(url),
    staleTime: 5 * 60 * 1000,
  });
  const cols = data?.columns || [];
  const rows = data?.rows || [];

  const copyCodes = () => { onCopy?.(); copyText(rows.map(r => r[cols[0].key]).join('\n')); };
  const copyTable = () => {
    onCopy?.();
    copyText([cols.map(c => c.label).join('\t'), ...rows.map(r => cols.map(c => rawCell(c, r[c.key])).join('\t'))].join('\n'));
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: t => t.zIndex.modal + 1 }} PaperProps={{ sx: { width: { xs: '100%', sm: 720 }, maxWidth: '100%' } }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>{title}</Typography>
          <IconButton size="small" onClick={onClose} aria-label="ปิด"><CloseIcon /></IconButton>
        </Box>
        {data?.generatedAt && (
          <Typography variant="caption" color="text.secondary">
            ข้อมูล ณ {new Date(data.generatedAt).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' })} · {rows.length} รายการ
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, my: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" disabled={!rows.length} onClick={copyCodes}>คัดลอกรหัส</Button>
          <Button size="small" variant="outlined" disabled={!rows.length} onClick={copyTable}>คัดลอกทั้งตาราง</Button>
          {onDownload && <Button size="small" variant="contained" onClick={onDownload}>ดาวน์โหลด Excel</Button>}
        </Box>
        {isLoading && <CircularProgress size={24} />}
        {error && <Alert severity="error">{error.response?.data?.error || error.message}</Alert>}
        {rows.length > 0 && (
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {cols.map(c => (
                    <TableCell key={c.key} align={['money', 'number', 'percent'].includes(c.type) ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap' }}>
                      {c.label}
                    </TableCell>
                  ))}
                  {rowAction && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} hover>
                    {cols.map(c => (
                      <TableCell key={c.key} align={['money', 'number', 'percent'].includes(c.type) ? 'right' : 'left'} sx={{ whiteSpace: c.type === 'text' ? 'normal' : 'nowrap' }}>
                        {formatCell(c, r[c.key])}
                      </TableCell>
                    ))}
                    {rowAction && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {doneRows.has(i)
                          ? <Typography variant="caption" color="text.secondary">{rowAction.doneLabel}</Typography>
                          : (
                            <Button size="small" disabled={rowAction.disabled} onClick={async () => {
                              if (await rowAction.onClick(r)) setDoneRows(prev => new Set(prev).add(i));
                            }}
                            >{rowAction.label}</Button>
                          )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
